import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
import { LanguageSchema } from "./column-schemas.js";
/**
 * A per-location environment field (`location_risk_fields`) — the admin
 * risk-center's location-environment editor lets a client add its own
 * area/object/activity values scoped to one location, beyond the shared
 * `risk_template_areas`/`objects`/`activities` catalog.
 */
export const LocationRiskFieldType = S.Literal("areas", "objects", "activities");
export const LocationRiskFieldRow = S.Struct({
    id: S.UUID,
    location_id: S.UUID,
    client_id: S.UUID,
    type: LocationRiskFieldType,
    translation_group_id: S.NullOr(S.Int),
    custom_name: S.NullOr(S.String),
    unique_label: S.NullOr(S.String),
    parent_id: S.NullOr(S.UUID),
    /** null when the creator's account is gone. */
    created_by: S.NullOr(S.UUID),
    date_created: S.NullOr(S.String),
});
export const LocationRiskFieldDetailRow = S.Struct({
    id: S.UUID,
    type: LocationRiskFieldType,
    unique_label: S.NullOr(S.String),
    custom_name: S.NullOr(S.String),
    translation_group_id: S.NullOr(S.Int),
    label: S.NullOr(S.String),
    parent_id: S.NullOr(S.UUID),
    parent_type: S.NullOr(S.String),
    parent_label: S.NullOr(S.String),
    child_count: S.Int,
});
export const LocationRiskFieldNotFound = S.Struct({
    _tag: S.Literal("LocationRiskFieldNotFound"),
});
export const ListLocationRiskFieldsParams = S.extend(PagingParams, S.Struct({
    client_id: S.UUID,
    location_id: S.UUID,
    type: S.optional(LocationRiskFieldType),
    language: S.optional(LanguageSchema),
}));
export const ListLocationRiskFields = HttpApiEndpoint.get("listLocationRiskFields", "/admin/location-risk-fields")
    .setUrlParams(ListLocationRiskFieldsParams)
    .addSuccess(pagedResponse(LocationRiskFieldDetailRow))
    .addError(Forbidden, { status: 403 });
export const CreateLocationRiskFieldPayload = S.Struct({
    client_id: S.UUID,
    location_id: S.UUID,
    type: LocationRiskFieldType,
    custom_name: S.optional(S.String),
    translation_group_id: S.optional(S.Int),
    unique_label: S.optional(S.String),
    parent_id: S.optional(S.UUID),
});
export const CreateLocationRiskField = HttpApiEndpoint.post("createLocationRiskField", "/admin/location-risk-fields/create")
    .setPayload(CreateLocationRiskFieldPayload)
    .addSuccess(S.Struct({ data: LocationRiskFieldRow }))
    .addError(Forbidden, { status: 403 });
export const UpdateLocationRiskFieldPayload = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
    location_id: S.optional(S.UUID),
    type: S.optional(LocationRiskFieldType),
    custom_name: S.optional(S.NullOr(S.String)),
    translation_group_id: S.optional(S.NullOr(S.Int)),
    unique_label: S.optional(S.NullOr(S.String)),
    parent_id: S.optional(S.NullOr(S.UUID)),
});
export const UpdateLocationRiskField = HttpApiEndpoint.post("updateLocationRiskField", "/admin/location-risk-fields/update")
    .setPayload(UpdateLocationRiskFieldPayload)
    .addSuccess(S.Struct({ data: LocationRiskFieldRow }))
    .addError(LocationRiskFieldNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const DeleteLocationRiskFieldPayload = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
});
export const DeleteLocationRiskField = HttpApiEndpoint.post("deleteLocationRiskField", "/admin/location-risk-fields/delete")
    .setPayload(DeleteLocationRiskFieldPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(LocationRiskFieldNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
export const adminLocationRiskFieldsGroup = HttpApiGroup.make("admin-location-risk-fields")
    .add(ListLocationRiskFields)
    .add(CreateLocationRiskField)
    .add(UpdateLocationRiskField)
    .add(DeleteLocationRiskField)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=admin-location-risk-fields.js.map